/**
 * Plan Service - Subscription Plan Permissions & Limits
 *
 * This module handles all plan-related permission checks for the CliniKit app.
 * It checks if users can perform actions based on their subscription plan.
 *
 * Plans:
 * - Trial: Limited functionality for new users (default)
 * - Starter: Solo plan, up to 10 patients
 * - Starter-Plus: Solo plan, up to 15 patients
 * - Premium: Full access, multi-therapist clinic
 * - Pilot (custom1): Free pilot program — identical capabilities to Premium
 * - Custom2–Custom10: Reserved for future custom groups (same as Premium)
 *
 * Error Code Range: CK-9900 - CK-9949
 */

import { doc, getDoc, collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

// ============================================================
// PLAN CONFIGURATION
// ============================================================

/**
 * Plan limits configuration lives in ./planLimits (single source of truth,
 * also handles per-clinic overrides). Re-exported here for backwards
 * compatibility with existing imports.
 */
import {
  PLAN_LIMITS,
  getPlanLimits,
  getEffectiveLimits,
  getPlanDefaults,
  diffFromPlan,
  isUnlimited,
  UNLIMITED,
} from "./planLimits";

export { PLAN_LIMITS, getPlanLimits, getEffectiveLimits, getPlanDefaults, diffFromPlan, isUnlimited, UNLIMITED };

// Limit types for UI messages
export const LIMIT_TYPES = {
  SMS: "sms",
  AI_ACTIONS: "ai_actions",
  ROOMS: "rooms",
  THERAPISTS: "therapists",
  PATIENTS: "patients",
  MORNING_INTEGRATION: "morning_integration",
  DIGITAL_INVOICE_INTEGRATION: "digital_invoice_integration",
  GROW_INTEGRATION: "grow_integration",
  WRITE_OPS: "write_ops",
};

// Hebrew messages for each limit type
export const LIMIT_MESSAGES = {
  [LIMIT_TYPES.SMS]: {
    title: "הגעת למגבלת הודעות ה-SMS",
    description: "מכסת הודעות ה-SMS החודשית בתוכנית שלך מוצתה.",
    icon: "ChatText",
  },
  [LIMIT_TYPES.AI_ACTIONS]: {
    title: "הגעת למגבלת פעולות ה-AI",
    description: "מכסת פעולות ה-AI החודשית בתוכנית שלך מוצתה.",
    icon: "Brain",
  },
  [LIMIT_TYPES.ROOMS]: {
    title: "יצירת חדרים אינה זמינה",
    description: "התוכנית הנוכחית שלך אינה כוללת אפשרות ליצור חדרי טיפול.",
    icon: "Door",
  },
  [LIMIT_TYPES.THERAPISTS]: {
    title: "הוספת מטפלים אינה זמינה",
    description: "התוכנית הנוכחית שלך מוגבלת לקליניקה עצמאית (מטפל יחיד).",
    icon: "UserPlus",
  },
  [LIMIT_TYPES.PATIENTS]: {
    title: "הגעת למגבלת המטופלים",
    description: "הגעת למספר המטופלים המקסימלי בתוכנית שלך.",
    icon: "Users",
  },
  [LIMIT_TYPES.MORNING_INTEGRATION]: {
    title: "אינטגרציה לחשבונית ירוקה אינה זמינה",
    description: "התוכנית הנוכחית שלך אינה כוללת אינטגרציה למערכת חשבוניות.",
    icon: "Receipt",
  },
  [LIMIT_TYPES.GROW_INTEGRATION]: {
    title: "חיבור Grow אינו זמין במסלול הנוכחי",
    description: "התוכנית הנוכחית שלך אינה כוללת אינטגרציה למערכת חשבוניות.",
    icon: "Receipt",
  },
  [LIMIT_TYPES.DIGITAL_INVOICE_INTEGRATION]: {
    title: "אינטגרציה לחשבונית דיגיטלית אינה זמינה",
    description: "התוכנית הנוכחית שלך אינה כוללת אינטגרציה למערכת חשבוניות.",
    icon: "Receipt",
  },
  [LIMIT_TYPES.WRITE_OPS]: {
    title: "הגעת למגבלת הפעולות בתקופת הניסיון",
    description: "מכסת הפעולות בתקופת הניסיון מוצתה. שדרג את התוכנית כדי להמשיך להשתמש במערכת.",
    icon: "PencilLine",
  },
};

// ============================================================
// CACHE MANAGEMENT
// ============================================================

// Cache for usage data to reduce Firestore reads
const usageCache = new Map();
const CACHE_TTL = 60000; // 1 minute cache

/**
 * Clear the usage cache for a clinic
 * Call this after performing an action that changes usage
 */
export const clearUsageCache = (clinicId) => {
  if (clinicId) {
    usageCache.delete(clinicId);
  }
};

/**
 * Clear all cached data
 */
export const clearAllUsageCache = () => {
  usageCache.clear();
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Gets the current month key in YYYY-MM format (Israel timezone)
 */
const getCurrentMonthKey = () => {
  const now = new Date();
  const israelDate = new Date(
    now.toLocaleString("en-US", { timeZone: "Asia/Jerusalem" })
  );
  const year = israelDate.getFullYear();
  const month = String(israelDate.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

// ============================================================
// USAGE DATA FETCHING
// ============================================================

/**
 * Fetch current usage data for a clinic
 * Returns cached data if available and fresh
 */
export const getClinicUsageData = async (clinicId) => {
  if (!clinicId) {
    console.error("[CK-9900] Missing clinicId for usage check");
    return null;
  }

  // Check cache first
  const cached = usageCache.get(clinicId);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }

  try {
    const monthKey = getCurrentMonthKey();

    // Fetch clinic data
    const clinicRef = doc(db, "clinics", clinicId);
    const clinicSnap = await getDoc(clinicRef);

    if (!clinicSnap.exists()) {
      console.error("[CK-9901] Clinic not found:", clinicId);
      return null;
    }

    const clinicData = clinicSnap.data();

    // Fetch monthly usage
    const monthlyUsageRef = doc(db, "clinics", clinicId, "monthlyUsage", monthKey);
    const monthlyUsageSnap = await getDoc(monthlyUsageRef);
    const monthlyUsage = monthlyUsageSnap.exists() ? monthlyUsageSnap.data() : {};

    // Count patients
    const patientsRef = collection(db, "clinics", clinicId, "patients");
    const patientsSnap = await getDocs(patientsRef);
    const patientCount = patientsSnap.size;

    // Count active rooms
    const roomsRef = collection(db, "clinics", clinicId, "rooms");
    const activeRoomsQuery = query(roomsRef, where("isActive", "!=", false));
    const roomsSnap = await getDocs(activeRoomsQuery);
    const roomCount = roomsSnap.size;

    // Count seats: owner (never stored in clinic.therapists[]) + ACTIVE added
    // members. Deactivated members free their seat (server mirror: CK-9790).
    const therapistCount =
      1 + (clinicData.therapists || []).filter((t) => t && t.isActive !== false).length;

    const usageData = {
      plan: clinicData.plan || "trial",
      // SMS usage - from monthlyUsage (single source of truth)
      smsUsedThisMonth: monthlyUsage.smsSentCount || 0,
      // AI usage
      aiActionsThisMonth: monthlyUsage.aiRequestsCount || 0,
      // Counts
      patientCount,
      roomCount,
      therapistCount,
      // Trial write operations (stored on clinic doc)
      trialWriteOps: clinicData._trialWriteOps || 0,
      // Morning integration
      isMorningIntegrated: clinicData.isMorningPaymentSystemIntegrated || false,
      // Per-clinic limit overrides (NiV) — consumed by getEffectiveLimits
      planOverrides: clinicData.planOverrides || null,
      videoMeetingMinutesLimit: clinicData.videoMeetingMinutesLimit ?? null,
      aiTreatmentsReportMonthlyLimit: clinicData.aiTreatmentsReportMonthlyLimit ?? null,
      aiTreatmentsReportEnabled: clinicData.aiTreatmentsReportEnabled ?? null,
      allowedStorageInMbMaxPerClinic: clinicData.allowedStorageInMbMaxPerClinic ?? null,
    };

    // Cache the result
    usageCache.set(clinicId, {
      data: usageData,
      timestamp: Date.now(),
    });

    return usageData;
  } catch (error) {
    console.error("[CK-9902] Error fetching usage data:", {
      errorCode: "CK-9902",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    return null;
  }
};

// ============================================================
// PERMISSION CHECK FUNCTIONS
// ============================================================

/**
 * Check if clinic can send SMS
 * @returns {{ allowed: boolean, limitType: string | null, current: number, limit: number }}
 */
export const canSendSMS = async (clinicId) => {
  const usageData = await getClinicUsageData(clinicId);
  if (!usageData) {
    return { allowed: true, limitType: null }; // Fail open
  }

  const limits = getEffectiveLimits(usageData.plan, usageData);
  const current = usageData.smsUsedThisMonth;
  const limit = limits.smsPerMonth;

  if (current >= limit) {
    return {
      allowed: false,
      limitType: LIMIT_TYPES.SMS,
      current,
      limit,
      plan: usageData.plan,
    };
  }

  return { allowed: true, limitType: null, current, limit };
};

/**
 * Check if clinic can perform AI action
 * @returns {{ allowed: boolean, limitType: string | null, current: number, limit: number }}
 */
export const canUseAI = async (clinicId) => {
  const usageData = await getClinicUsageData(clinicId);
  if (!usageData) {
    return { allowed: true, limitType: null }; // Fail open
  }

  const limits = getEffectiveLimits(usageData.plan, usageData);
  const current = usageData.aiActionsThisMonth;
  const limit = limits.aiActionsPerMonth;

  if (current >= limit) {
    return {
      allowed: false,
      limitType: LIMIT_TYPES.AI_ACTIONS,
      current,
      limit,
      plan: usageData.plan,
    };
  }

  return { allowed: true, limitType: null, current, limit };
};

/**
 * Check if clinic can create a room
 * @returns {{ allowed: boolean, limitType: string | null, current: number, limit: number }}
 */
export const canCreateRoom = async (clinicId) => {
  const usageData = await getClinicUsageData(clinicId);
  if (!usageData) {
    return { allowed: true, limitType: null }; // Fail open
  }

  const limits = getEffectiveLimits(usageData.plan, usageData);
  const current = usageData.roomCount;
  const limit = limits.maxRooms;

  if (current >= limit) {
    return {
      allowed: false,
      limitType: LIMIT_TYPES.ROOMS,
      current,
      limit,
      plan: usageData.plan,
    };
  }

  return { allowed: true, limitType: null, current, limit };
};

/**
 * Check if clinic can add a therapist
 * @returns {{ allowed: boolean, limitType: string | null, current: number, limit: number }}
 */
export const canAddTherapist = async (clinicId) => {
  const usageData = await getClinicUsageData(clinicId);
  if (!usageData) {
    return { allowed: true, limitType: null }; // Fail open
  }

  const limits = getEffectiveLimits(usageData.plan, usageData);
  const current = usageData.therapistCount;
  const limit = limits.maxTherapists;

  if (current >= limit) {
    return {
      allowed: false,
      limitType: LIMIT_TYPES.THERAPISTS,
      current,
      limit,
      plan: usageData.plan,
    };
  }

  return { allowed: true, limitType: null, current, limit };
};

/**
 * Check if clinic can create a patient
 * @returns {{ allowed: boolean, limitType: string | null, current: number, limit: number }}
 */
export const canCreatePatient = async (clinicId) => {
  const usageData = await getClinicUsageData(clinicId);
  if (!usageData) {
    return { allowed: true, limitType: null }; // Fail open
  }

  const limits = getEffectiveLimits(usageData.plan, usageData);
  const current = usageData.patientCount;
  const limit = limits.maxPatients;

  if (current >= limit) {
    return {
      allowed: false,
      limitType: LIMIT_TYPES.PATIENTS,
      current,
      limit,
      plan: usageData.plan,
    };
  }

  return { allowed: true, limitType: null, current, limit };
};

/**
 * Check if clinic can integrate with Morning (Green Invoice)
 * @returns {{ allowed: boolean, limitType: string | null }}
 */
export const canIntegrateMorning = async (clinicId) => {
  const usageData = await getClinicUsageData(clinicId);
  if (!usageData) {
    return { allowed: true, limitType: null }; // Fail open
  }

  const limits = getEffectiveLimits(usageData.plan, usageData);

  if (!limits.canIntegrateMorning) {
    return {
      allowed: false,
      limitType: LIMIT_TYPES.MORNING_INTEGRATION,
      plan: usageData.plan,
    };
  }

  return { allowed: true, limitType: null };
};

/**
 * Check if clinic can connect Grow payments (plan flag `canIntegrateGrow`).
 * All paid plans + trial have it; NiV may turn it off per clinic via planOverrides.
 *
 * @param {string} clinicId
 * @returns {{ allowed: boolean, limitType: string | null }}
 */
export const canIntegrateGrow = async (clinicId) => {
  const usageData = await getClinicUsageData(clinicId);
  if (!usageData) {
    return { allowed: true, limitType: null }; // Fail open
  }

  const limits = getEffectiveLimits(usageData.plan, usageData);

  if (!limits.canIntegrateGrow) {
    return {
      allowed: false,
      limitType: LIMIT_TYPES.GROW_INTEGRATION,
      plan: usageData.plan,
    };
  }

  return { allowed: true, limitType: null };
};

/**
 * Check if clinic can integrate with Digital Invoice (חשבונית דיגיטלית)
 * Uses the same plan gate as Morning integration.
 */
export const canIntegrateDigitalInvoice = async (clinicId) => {
  const usageData = await getClinicUsageData(clinicId);
  if (!usageData) {
    return { allowed: true, limitType: null };
  }

  const limits = getEffectiveLimits(usageData.plan, usageData);

  if (!limits.canIntegrateMorning) {
    return {
      allowed: false,
      limitType: LIMIT_TYPES.DIGITAL_INVOICE_INTEGRATION,
      plan: usageData.plan,
    };
  }

  return { allowed: true, limitType: null };
};

/**
 * Check if a trial clinic can perform a write operation
 * Only enforced for trial plans — paid plans have no write limit.
 * @returns {{ allowed: boolean, limitType: string | null, current: number, limit: number }}
 */
export const canPerformWrite = async (clinicId) => {
  const usageData = await getClinicUsageData(clinicId);
  if (!usageData) {
    return { allowed: true, limitType: null }; // Fail open
  }

  if (usageData.plan !== "trial") {
    return { allowed: true, limitType: null };
  }

  const limits = getEffectiveLimits(usageData.plan, usageData);
  const current = usageData.trialWriteOps;
  const limit = limits.maxWriteOpsPerTrial || 3000;

  if (current >= limit) {
    return {
      allowed: false,
      limitType: LIMIT_TYPES.WRITE_OPS,
      current,
      limit,
      plan: usageData.plan,
    };
  }

  return { allowed: true, limitType: null, current, limit };
};

/**
 * Generic permission check - checks any limit type
 * @param {string} clinicId - The clinic ID
 * @param {string} limitType - One of LIMIT_TYPES
 * @returns {{ allowed: boolean, limitType: string | null, current?: number, limit?: number }}
 */
export const checkPermission = async (clinicId, limitType) => {
  switch (limitType) {
    case LIMIT_TYPES.SMS:
      return canSendSMS(clinicId);
    case LIMIT_TYPES.AI_ACTIONS:
      return canUseAI(clinicId);
    case LIMIT_TYPES.ROOMS:
      return canCreateRoom(clinicId);
    case LIMIT_TYPES.THERAPISTS:
      return canAddTherapist(clinicId);
    case LIMIT_TYPES.PATIENTS:
      return canCreatePatient(clinicId);
    case LIMIT_TYPES.GROW_INTEGRATION:
      return canIntegrateGrow(clinicId);
    case LIMIT_TYPES.MORNING_INTEGRATION:
      return canIntegrateMorning(clinicId);
    case LIMIT_TYPES.DIGITAL_INVOICE_INTEGRATION:
      return canIntegrateDigitalInvoice(clinicId);
    case LIMIT_TYPES.WRITE_OPS:
      return canPerformWrite(clinicId);
    default:
      console.warn("[CK-9903] Unknown limit type:", limitType);
      return { allowed: true, limitType: null };
  }
};

/**
 * Get all current usage and limits for display
 * Useful for settings/dashboard pages
 */
export const getUsageSummary = async (clinicId) => {
  const usageData = await getClinicUsageData(clinicId);
  if (!usageData) {
    return null;
  }

  const limits = getEffectiveLimits(usageData.plan, usageData);

  return {
    plan: {
      name: limits.name,
      nameHebrew: limits.nameHebrew,
      key: usageData.plan,
    },
    usage: {
      sms: {
        current: usageData.smsUsedThisMonth,
        limit: limits.smsPerMonth,
        percentage: Math.round((usageData.smsUsedThisMonth / limits.smsPerMonth) * 100),
      },
      ai: {
        current: usageData.aiActionsThisMonth,
        limit: limits.aiActionsPerMonth,
        percentage: Math.round((usageData.aiActionsThisMonth / limits.aiActionsPerMonth) * 100),
      },
      patients: {
        current: usageData.patientCount,
        limit: limits.maxPatients,
        percentage: Math.round((usageData.patientCount / limits.maxPatients) * 100),
      },
      rooms: {
        current: usageData.roomCount,
        limit: limits.maxRooms,
        canCreate: limits.maxRooms > 0,
      },
      therapists: {
        current: usageData.therapistCount,
        limit: limits.maxTherapists,
        canAdd: limits.maxTherapists > 1,
      },
    },
    features: {
      morningIntegration: limits.canIntegrateMorning,
    },
  };
};
